import React from 'react'
import { Form, Modal } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { InfoCard2 } from '../../../widgets/components/UI/InfoCard2'
import { Field, Formik } from 'formik'
import { SelectInput } from '../../../widgets/components/Input/SelectInput'
import TextInput from '../../../widgets/components/Input/TextInput'
import * as Yup from 'yup'
import { industryOptions } from '../../../../core/_constants'
import { useAuth } from '../../../auth'
import { createProfessionalExperiences, updateProfessionalExperiences } from '../core/_requests'
import { toast } from 'react-toastify'
import { useSearchParams } from 'react-router-dom'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../../hooks/gtmHelpers'
import brandingConfig from '../../../../../config/brandingConfig'

const AddExperienceModal = ({
  isActive,
  setIsActive,
  setPersonalExp,
  currentExp,
  founderDetails,
}: any) => {
  const { formatMessage } = useIntl()
  const { companyId, currentUser } = useAuth()
  const [searchParams] = useSearchParams()
  const edit = searchParams.get('edit')

  const userSchema = Yup.object().shape({
    companyName: Yup.string().required('Company Name is required'),
    startDate: Yup.date().required('Start Date is required'),
    title: Yup.string().required('Title is required'),
  })

  const initialValues = currentExp || {
    companyName: null,
    industry: null,
    isInCurrentCompany: false,
    startDate: null,
    endDate: null,
    title: null,
    location: null,
  }

  function handleIsWorking(formik: any) {
    const newIsWorking = !formik.values.isInCurrentCompany
    formik.setFieldValue('isInCurrentCompany', newIsWorking)
    if (newIsWorking) {
      formik.setFieldValue('endDate', '')
    }
  }

  const handleSubmit = async (values: any) => {
    if (currentExp) {
      setPersonalExp((prevExp: any) =>
        prevExp.map((exp: any) => (exp.id === currentExp.id ? { ...values, id: currentExp.id } : exp))
      )
    } else {
      let newExperience: any
      if ((!founderDetails && edit === null) || (!founderDetails && edit)) {
        newExperience = {
          id: Date.now(),
          ...values,
        }
        setPersonalExp((prevExp: any) => [...prevExp, newExperience])
      } else {
        setPersonalExp((prevExp: any) => [...prevExp, values])
      }
    }
    setIsActive(false)
    if (founderDetails && !edit) {
      const rawPayload = {
        title: values.title,
        industry: parseInt(values?.industry),
        startDate: values?.startDate,
        isInCurrentCompany: values?.isInCurrentCompany,
        location: values?.location,
        companyName: values?.companyName,
        founderPromoterId: founderDetails?.founderPromoterId,
      }
      const payload = Object.fromEntries(
        Object.entries(rawPayload).filter(([_, value]) => value !== null && value !== undefined)
      )

      try {
        if (!companyId || !founderDetails) return
        if (currentExp === null) {
          const {
            data: { success },
          } = await createProfessionalExperiences(payload, companyId)

          if (success) {
            toast.success(formatMessage({ id: 'Professional experiences created successfully' }))
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.CreateProfessionalExpSuccessful,
                action: 'normal',
                metadata: {
                  company_id: companyId,
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  title: values.title,
                  industry: parseInt(values?.industry),
                  start_date: values?.startDate,
                  is_in_current_company: values?.isInCurrentCompany,
                  location: values?.location,
                  company_name: values?.companyName,
                  founder_promoter_id: founderDetails?.founderPromoterId,
                },
              },
            })
          }
        } else {
          const {
            data: { success },
          } = await updateProfessionalExperiences(payload, companyId, currentExp?.id)

          if (success) {
            toast.success(formatMessage({ id: 'Professional experiences updated successfully' }))
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.UpdateProfessionalExpSuccessful,
                action: 'normal',
                metadata: {
                  company_id: companyId,
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  title: values.title,
                  exp_id: currentExp?.id,
                  industry: parseInt(values?.industry),
                  start_date: values?.startDate,
                  is_in_current_company: values?.isInCurrentCompany,
                },
              },
            })
          }
        }
      } catch (err) {
        console.error('Error:', err)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.CreateProfessionalExpFailed,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              title: values.title,
              industry: parseInt(values?.industry),
              start_date: values?.startDate,
              error: err,
            },
          },
        })
      }
    }
  }

  return (
    <>
      <Modal show={isActive} onHide={() => setIsActive(false)} size='lg' fullscreen>
        <Modal.Header closeButton>
          <h2>{formatMessage({ id: 'Add Experience' })}</h2>
        </Modal.Header>
        <Modal.Body className='container'>
          <>
            <InfoCard2
              title={formatMessage({ id: 'How to Add a Share Transaction?' })}
              desc={formatMessage({
                id: `Add share transaction details, including Share Class, Date of Issue, Certificate Code, Number of Shares, and relevant documents on ${brandingConfig.brandName}. This information is crucial for managing deals and relationships with shareholders and investors.`,
              })}
              slug={brandingConfig.helpLinks.addFounderPromoterExperienceModal}
              className='w-100 mb-0'
            />
            <div className='w-100 mt-10'>
              <Formik
                initialValues={initialValues}
                validationSchema={userSchema}
                onSubmit={handleSubmit}
              >
                {(formik) => {
                  console.log(formik.values, 'formik')
                  return (
                    <Form onSubmit={formik.handleSubmit}>
                      <div className='row'>
                        <div className='col-md-6 col-12'>
                          <TextInput
                            fieldType='text'
                            label={formatMessage({ id: 'Company Name' })}
                            fieldName='companyName'
                            formik={formik}
                            placeholder={formatMessage({ id: 'Enter Company Name' })}
                            toolTipText={formatMessage({
                              id: 'Add the name of the company here to which the Founder/Promoter belongs.',
                            })}
                            isStarRequired={true}
                            isTooltipForModal={true}
                          />
                        </div>
                        <div className='col-md-6 col-12'>
                          <SelectInput
                            label={formatMessage({ id: 'Industry' })}
                            fieldName='industry'
                            placeholder={formatMessage({ id: 'Select Industry' })}
                            formik={formik}
                            toolTipText={formatMessage({
                              id: 'Select the industry to which the added company belongs.',
                            })}
                            options={industryOptions}
                            isTooltipForModal={true}
                            isStarRequired={true}
                          />
                        </div>
                        <div className='col-md-12 col-12'>
                          <Field
                            type='checkbox'
                            name='isInCurrentCompany'
                            className='form-check-input mb-8'
                            checked={formik.values.isInCurrentCompany}
                            onChange={() => handleIsWorking(formik)}
                          />
                          <label className='form-check-label ms-2 fs-6'>
                            {formatMessage({ id: 'This person currently works here.' })}
                          </label>
                        </div>
                        <div className='col-md-6 col-12'>
                          <TextInput
                            fieldType='date'
                            label={formatMessage({ id: 'Start Date' })}
                            fieldName='startDate'
                            formik={formik}
                            placeholder={formatMessage({ id: 'Enter Start Date' })}
                            toolTipText={formatMessage({
                              id: 'Add the date on which the respective Founder/Promoter joined this company.',
                            })}
                            isStarRequired={true}
                            isTooltipForModal={true}
                            isDateOfBirth={true}
                          />
                        </div>
                        <div className='col-md-6 col-12'>
                          <TextInput
                            fieldType='date'
                            label={formatMessage({ id: 'End Date' })}
                            fieldName='endDate'
                            formik={formik}
                            placeholder={formatMessage({ id: 'Enter End Date' })}
                            toolTipText={formatMessage({
                              id: 'If the added person is not part of the company anymore, add the end date of their tenure.',
                            })}
                            isStarRequired={false}
                            isTooltipForModal={true}
                            isDisabled={formik.values.isInCurrentCompany}
                            isDateOfBirth={true}
                          />
                        </div>
                        <div className='col-md-6 col-12'>
                          <TextInput
                            fieldType='text'
                            label={formatMessage({ id: 'Title' })}
                            fieldName='title'
                            formik={formik}
                            placeholder={formatMessage({ id: 'Enter Title' })}
                            toolTipText={formatMessage({
                              id: 'Add the title of the position the Founder/Promoter held in the added company.',
                            })}
                            isStarRequired={true}
                            isTooltipForModal={true}
                          />
                        </div>
                        <div className='col-md-6 col-12'>
                          <TextInput
                            fieldType='text'
                            label={formatMessage({ id: 'Location' })}
                            fieldName='location'
                            formik={formik}
                            placeholder={formatMessage({ id: 'Enter Location' })}
                            toolTipText={formatMessage({
                              id: 'Add the location of work of the Founder/Promotor.',
                            })}
                            isStarRequired={false}
                            isTooltipForModal={true}
                          />
                        </div>
                      </div>
                      <Modal.Footer className='p-md-0 px-0 d-flex container'>
                        <div className='d-flex my-md-4 mx-6 mx-md-0 my-0 mb-2 justify-content-md-end justify-content-center w-md-auto w-100 order-2 order-md-1 mb-2'>
                          <button
                            className='btn bg-light custom-button-property text-muted font-size-13 me-md-2 me-0 fw-normal w-100 w-md-auto'
                            style={{ padding: '15px 40px' }}
                            onClick={() => setIsActive(false)}
                            type='button'
                          >
                            <span className='indicator-label font-size-13 p-0'>
                              {formatMessage({ id: 'Cancel' })}
                            </span>
                          </button>
                        </div>
                        <div className='d-flex my-md-4 mx-6 mx-md-0 my-0 justify-content-md-end justify-content-center w-md-auto w-100 order-1 order-md-2 mb-2'>
                          <button
                            className='btn bg-primary custom-button-property text-white font-size-13 me-md-2 me-0 fw-normal w-100 w-md-auto'
                            style={{ padding: '15px 40px' }}
                            type='submit'
                            disabled={!formik.isValid}
                          >
                            <span className='indicator-label font-size-13 p-0'>
                              {formatMessage({ id: 'Save' })}
                            </span>
                          </button>
                        </div>
                      </Modal.Footer>
                    </Form>
                  )
                }}
              </Formik>
            </div>
          </>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default AddExperienceModal
